/**
 * «Borrar los datos de esta persona» (Ley 25.326, §13.4 bis): la supresión desde la conversación, que sirve
 * también para quien escribió sin número (la Base y Activa buscan por teléfono y no la encontraban). Sólo
 * la ve quien puede ejecutarla (`puedeSuprimir`). Es una tarjeta en la página, no un modal (R2).
 *
 * «Dejarla también en no contactar» arranca marcada: quien pide que se borren sus datos casi siempre
 * tampoco quiere mensajes. Si se puede dar la baja lo dice el backend (`bajaPosible`): sin número propio, la da
 * igual con el de la Base cuando contestó citando una campaña, y la casilla deshabilitada por `numero` dejaba a
 * esa persona recibiendo campañas.
 */

import { useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ConversacionDetalle, ResultadoSupresionConversacion } from "../../types.conversaciones";
import { llamar } from "../base/comun";
import { textoError } from "../../logic/respuesta";

export function TarjetaSuprimir({ conversacion, onCancelar, onListo }: { conversacion: ConversacionDetalle; onCancelar: () => void; onListo: () => void }) {
  const id = useId();
  const bajaPosible = conversacion.bajaPosible;
  const [dejarEnNoContactar, setDejar] = useState(true);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const enCurso = useRef(false);

  const borrar = async () => {
    if (enCurso.current || !motivo.trim()) return;
    enCurso.current = true;
    setEnviando(true);
    setError("");
    const l = await llamar<ResultadoSupresionConversacion>(() => conversacionesApi.suprimir(conversacion.id, {
      motivo: motivo.trim(), dejarEnNoContactar: bajaPosible && dejarEnNoContactar,
    }));
    enCurso.current = false;
    setEnviando(false);
    if (!l.ok) { setError(textoError(l)); return; }
    onListo();
  };

  return (
    <section className="rounded-2xl border border-red-200 bg-white p-4 text-sm text-slate-700" aria-labelledby={`${id}-titulo`}>
      <h3 id={`${id}-titulo`} className="font-semibold text-red-800">Borrar los datos de esta persona</h3>
      <p className="mt-2">
        Es para cuando la persona pide que se borren sus datos (Ley 25.326). Se borran sus mensajes, su nombre, sus archivos y su
        número de todo el WhatsApp oficial de la marca, y queda una constancia sin sus datos. No se puede deshacer.
      </p>
      <label className="mt-3 flex items-start gap-2">
        <input type="checkbox" checked={bajaPosible && dejarEnNoContactar} disabled={!bajaPosible || enviando}
          onChange={(e) => setDejar(e.target.checked)} className="mt-0.5" />
        <span>Dejarla también en no contactar</span>
      </label>
      {!bajaPosible && <p className="ml-6 text-xs text-slate-500">Sin número de teléfono no se puede registrar la baja.</p>}
      <label htmlFor={`${id}-motivo`} className="mt-3 block text-xs font-semibold text-slate-600">Quién lo pidió y cómo</label>
      <textarea id={`${id}-motivo`} value={motivo} maxLength={300} rows={2} required onChange={(e) => setMotivo(e.target.value)}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
      {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => void borrar()} disabled={enviando || !motivo.trim()}
          className="h-9 px-4 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
          {enviando && <Loader2 size={14} className="animate-spin" />} Borrar los datos
        </button>
        <button type="button" onClick={onCancelar} disabled={enviando} className="h-9 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold">Cancelar</button>
      </div>
    </section>
  );
}

export default TarjetaSuprimir;
